import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarCheck,
  CalendarClock,
  CheckCircle2,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import {
  fetchEvenementsBureau,
  createEvenement,
  updateEvenement,
  deleteEvenement,
  type EvenementPayload,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { nomEdition, nomSansMillesime } from '../utils/evenement';

import { dateHeureFr } from '../utils/dates';
const statuts = [
  { value: 'brouillon', label: 'Brouillon', classe: 'bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-700' },
  { value: 'publie', label: 'Publié', classe: 'bg-green-100 text-green-800' },
  { value: 'termine', label: 'Terminé', classe: 'bg-blue-100 text-blue-800' },
  { value: 'annule', label: 'Annulé', classe: 'bg-red-100 text-red-800' },
];

const formVide: EvenementPayload = {
  nom: '',
  dateDebut: '',
  dateFin: '',
  lieu: '',
  description: '',
  statut: 'brouillon',
};

/** Les champs date attendent le format accepté par datetime-local. */
const pourChamp = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');

export default function BureauEvenementsPage() {
  const { user, isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<EvenementPayload>(formVide);
  const [enEdition, setEnEdition] = useState<number | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Deux droits distincts : voir l'ecran, et y agir. La grille peut ouvrir la
  // consultation a tout le bureau sans pour autant laisser creer une
  // festivite — ce qui engage le comite reste au president, au depart. La
  // suppression demeure reservee a l'administrateur.
  const peutVoir = aLeDroit('voir_evenements');
  const peutGerer = user?.peutGererEvenements === true;
  const isAdmin = user?.roles?.includes('ROLE_ADMIN');

  const query = useQuery({
    queryKey: ['bureau-evenements'],
    queryFn: fetchEvenementsBureau,
    enabled: peutVoir,
  });

  const fermer = (message: string) => {
    setNotice({ type: 'success', message });
    setOuvert(false);
    setEnEdition(null);
    setForm(formVide);
    queryClient.invalidateQueries({ queryKey: ['bureau-evenements'] });
  };
  const echec = (error: Error) => setNotice({ type: 'error', message: error.message });

  const creer = useMutation({
    mutationFn: () => createEvenement(form),
    onSuccess: () => fermer('Événement créé.'),
    onError: echec,
  });

  const modifier = useMutation({
    mutationFn: () => updateEvenement(enEdition as number, form),
    onSuccess: () => fermer('Événement mis à jour.'),
    onError: echec,
  });

  const supprimer = useMutation({
    mutationFn: deleteEvenement,
    onSuccess: () => fermer('Événement supprimé.'),
    onError: echec,
  });

  if (authLoading || query.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              La consultation des événements dépend de votre fonction au bureau. Un administrateur peut l'ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const evenements = query.data?.evenements ?? [];
  const enCours = creer.isPending || modifier.isPending;
  // Une fin antérieure au début fausse tout ce qui s'appuie sur la durée : la
  // grille de planning n'affiche plus aucune journée. Le serveur le refuse ;
  // l'écran le dit avant l'envoi.
  const datesIncoherentes =
    (form.dateFin ?? '') !== '' && form.dateDebut !== '' && (form.dateFin ?? '') <= form.dateDebut;

  const invalide = !form.nom.trim() || !form.dateDebut || datesIncoherentes;

  const ouvrirEdition = (e: any) => {
    setForm({
      nom: e.nom ?? '',
      dateDebut: pourChamp(e.dateDebut),
      dateFin: pourChamp(e.dateFin),
      lieu: e.lieu ?? '',
      description: e.description ?? '',
      statut: e.statut ?? 'brouillon',
    });
    setEnEdition(e.id);
    setOuvert(true);
    setNotice(null);
  };

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <CalendarCheck className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Événements</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Les festivités du comité. Seuls les événements publiés apparaissent sur le site.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card flex items-start gap-3 text-sm ${
            notice.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <span />
        {peutGerer && (
          <button
            onClick={() => {
              setForm(formVide);
              setEnEdition(null);
              setOuvert(!ouvert);
              setNotice(null);
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nouvel événement
          </button>
        )}
      </div>

      {ouvert && (
        <div className="card lg:p-5 space-y-3">
          <h2 className="font-semibold">
            {enEdition !== null ? "Modifier l'événement" : 'Nouvel événement'}
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="lg:col-span-2">
              {/* L'année ne fait pas partie du nom : « Fête votive », pas
                  « Fête votive 2026 ». C'est ce qui rapproche les éditions
                  d'une année sur l'autre — l'affichage recompose le millésime
                  depuis la date de programmation. La liste propose les noms
                  déjà portés, débarrassés de leur année. */}
              <input
                className="input w-full"
                list="noms-evenements"
                placeholder="Nom de l'événement — sans l'année, ex. Fête votive"
                value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
              />
              <datalist id="noms-evenements">
                {[...new Set(evenements.map((ev: any) => nomSansMillesime(String(ev.nom))))]
                  .filter((nom): nom is string => nom !== '')
                  .sort()
                  .map((nom) => (
                    <option key={nom} value={nom} />
                  ))}
              </datalist>
            </div>
            <label className="text-sm text-gray-600 dark:text-gray-400">
              Début
              <input
                className="input mt-1"
                type="datetime-local"
                value={form.dateDebut}
                onChange={(e) => setForm({ ...form, dateDebut: e.target.value })}
              />
            </label>
            <label className="text-sm text-gray-600 dark:text-gray-400">
              Fin (facultatif)
              <input
                className={`input mt-1 ${datesIncoherentes ? 'border-red-400' : ''}`}
                type="datetime-local"
                // Le sélecteur natif interdit déjà de descendre sous le début.
                min={form.dateDebut || undefined}
                value={form.dateFin ?? ''}
                onChange={(e) => setForm({ ...form, dateFin: e.target.value })}
              />
              {datesIncoherentes && (
                <span className="text-xs text-red-600">
                  La fin doit être postérieure au début.
                </span>
              )}
            </label>
            <input
              className="input"
              placeholder="Lieu"
              value={form.lieu ?? ''}
              onChange={(e) => setForm({ ...form, lieu: e.target.value })}
            />
            <select
              className="input"
              value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
            >
              {statuts.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <textarea
              className="input lg:col-span-2 min-h-[6rem]"
              placeholder="Description"
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => (enEdition !== null ? modifier.mutate() : creer.mutate())}
              disabled={enCours || invalide}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
              {enEdition !== null ? 'Enregistrer' : "Créer l'événement"}
            </button>
            <button onClick={() => setOuvert(false)} className="text-sm text-gray-500 dark:text-gray-400">Annuler</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {evenements.length === 0 ? (
          <p className="text-gray-500 text-sm dark:text-gray-400">Aucun événement pour le moment.</p>
        ) : (
          evenements.map((e: any) => {
            const statut = statuts.find((s) => s.value === e.statut);
            return (
              <div key={e.id} className="card p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div className="text-sm space-y-1">
                  <p className="font-semibold text-gray-900 flex flex-wrap items-center gap-2 dark:text-gray-100">
                    {nomEdition(e.nom, e.dateDebut)}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statut?.classe ?? 'bg-gray-100'}`}>
                      {statut?.label ?? e.statut}
                    </span>
                  </p>
                  <p className="text-gray-600 dark:text-gray-400">
                    {dateHeureFr(e.dateDebut)}
                    {e.dateFin && ` → ${dateHeureFr(e.dateFin)}`}
                  </p>
                  {e.lieu && (
                    <p className="text-gray-500 flex items-center gap-1 dark:text-gray-400">
                      <MapPin className="w-4 h-4" /> {e.lieu}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  <Link
                    to={`/bureau/evenements/${e.id}`}
                    className="text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 text-sm dark:text-gray-300"
                  >
                    <Settings2 className="w-4 h-4" /> Gérer
                  </Link>
                  {/* Raccourci direct vers la grille : le planning est ce que
                      le bureau vient chercher le plus souvent, et l'atteindre
                      demandait d'ouvrir la fiche puis de changer d'onglet. */}
                  <Link
                    to={`/bureau/evenements/${e.id}?onglet=planning`}
                    className="text-gray-700 hover:text-gray-900 inline-flex items-center gap-1 text-sm dark:text-gray-300"
                  >
                    <CalendarClock className="w-4 h-4" /> Planning
                  </Link>
                  {peutGerer && (
                    <button
                      onClick={() => ouvrirEdition(e)}
                      className="text-primary-700 hover:text-primary-800 inline-flex items-center gap-1 text-sm"
                    >
                      <Pencil className="w-4 h-4" /> Modifier
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Supprimer définitivement « ${e.nom} » ?`)) {
                          supprimer.mutate(e.id);
                        }
                      }}
                      disabled={supprimer.isPending}
                      className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"
                    >
                      <Trash2 className="w-4 h-4" /> Supprimer
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
